"use client";

/**
 * ProtectedFeature — component-level auth gate.
 *
 * Renders children if the user is signed in.
 * Renders an inline auth prompt if not — no page-level redirect.
 *
 * Usage:
 *   <ProtectedFeature>
 *     <SendMessageButton />
 *   </ProtectedFeature>
 *
 *   <ProtectedFeature fallback={<CustomPrompt />}>
 *     <ExportButton />
 *   </ProtectedFeature>
 */
import type { ReactNode } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useAuthContext } from "./AuthContext";
import styles from "./ProtectedFeature.module.css";

interface Props {
  children: ReactNode;
  /** Override the default inline sign-in prompt */
  fallback?: ReactNode;
}

function DefaultAuthPrompt() {
  const router = useRouter();
  const pathname = usePathname();
  return (
    <button
      onClick={() => router.push(`/login?redirect_to=${encodeURIComponent(pathname)}`)}
      aria-label="Sign in required"
      className={styles.signInBtn}
    >
      <span aria-hidden="true">🔒</span>
      Sign in to use this feature
    </button>
  );
}

export function ProtectedFeature({ children, fallback }: Props) {
  const { user, loading } = useAuthContext();

  if (loading) return null;

  if (!user) {
    return <>{fallback ?? <DefaultAuthPrompt />}</>;
  }

  return <>{children}</>;
}
